import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUser, FiCamera, FiLoader, FiTwitter } from 'react-icons/fi';
import SafeIcon from './SafeIcon';
import { supabase } from '@/integrations/supabase/client';

/**
 * Minimal profile modal — Stripe / Linear / Notion style.
 *
 * Design goals:
 *   - No more than 5 fields visible (avatar, display name, bio, timezone,
 *     one social). Everything else that used to live here has moved:
 *       • phone/2FA        → Settings → Security (SecurityModal)
 *       • address/invoicing → Account → Billing
 *       • birthday          → removed (no product value)
 *       • company info      → removed (retail-facing, not a business tool)
 *   - Terminal V3 aesthetic to match the rest of the app.
 *   - Auto-saved draft in localStorage, single write to `user_profiles`
 *     on Save, avatar upload preserved from the old modal.
 */

const STORAGE_KEY = 'profile_draft_v2';

// Kept short and IANA-canonical — the ones traders actually use.
// If you want more, add here; the <select> auto-scrolls.
const TIMEZONES = [
  { value: 'America/New_York',     label: 'Eastern (EST/EDT) · NYC' },
  { value: 'America/Chicago',      label: 'Central (CST/CDT)' },
  { value: 'America/Denver',       label: 'Mountain (MST/MDT)' },
  { value: 'America/Los_Angeles',  label: 'Pacific (PST/PDT) · LA' },
  { value: 'America/Toronto',      label: 'Eastern · Toronto' },
  { value: 'America/Sao_Paulo',    label: 'Brasília · São Paulo' },
  { value: 'Europe/London',        label: 'London (GMT/BST)' },
  { value: 'Europe/Paris',         label: 'Paris · Berlin · Madrid' },
  { value: 'Europe/Zurich',        label: 'Zurich · Frankfurt' },
  { value: 'Africa/Algiers',       label: 'Algiers · Tunis · Casablanca' },
  { value: 'Asia/Dubai',           label: 'Dubai · Abu Dhabi' },
  { value: 'Asia/Kolkata',         label: 'India (IST)' },
  { value: 'Asia/Singapore',       label: 'Singapore · Hong Kong' },
  { value: 'Asia/Tokyo',           label: 'Tokyo · Seoul' },
  { value: 'Australia/Sydney',     label: 'Sydney · Melbourne' },
];

const BIO_MAX = 140;

const ProfileModal = ({ isOpen, onClose, userProfile, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [status, setStatus] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    display_name: '',
    bio: '',
    timezone: '',
    twitter: '',
  });

  // ── Draft persistence ────────────────────────────────────────────
  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      if (parsed.userId === userProfile?.user_id) return parsed.data;
    } catch {
      /* ignore */
    }
    return null;
  };

  const saveDraft = (data) => {
    try {
      if (!userProfile?.user_id) return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        userId: userProfile.user_id,
        data,
        savedAt: Date.now(),
      }));
    } catch {
      /* ignore */
    }
  };

  const clearDraft = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  // ── Hydrate on open ──────────────────────────────────────────────
  useEffect(() => {
    if (!userProfile) return;
    const draft = loadDraft();
    if (draft) {
      setFormData(draft);
    } else {
      const first = userProfile.first_name || '';
      const last  = userProfile.last_name  || '';
      const displayName = userProfile.name?.trim() || `${first} ${last}`.trim() || (userProfile.email?.split('@')[0] ?? '');
      setFormData({
        display_name: displayName,
        bio: userProfile.personal_profile || '',
        timezone: userProfile.timezone || '',
        twitter: userProfile.social?.twitter || '',
      });
    }
    setAvatarUrl(userProfile.avatar_url || '');
  }, [userProfile]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      saveDraft(next);
      return next;
    });
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !userProfile?.user_id) return;

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setStatus({ type: 'error', message: 'Please upload a JPEG, PNG, WebP or GIF.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ type: 'error', message: 'Image must be less than 5 MB.' });
      return;
    }

    setUploadingAvatar(true);
    setStatus(null);
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${userProfile.user_id}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      const cachedUrl = `${publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ avatar_url: cachedUrl, updated_at: new Date().toISOString() })
        .eq('user_id', userProfile.user_id);
      if (updateError) throw updateError;

      setAvatarUrl(cachedUrl);
      setStatus({ type: 'success', message: 'Photo updated.' });
    } catch (error) {
      console.error('Avatar upload error:', error);
      setStatus({ type: 'error', message: error.message || 'Failed to upload photo.' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      // Split display_name back into first/last so downstream code
      // that still reads first_name/last_name keeps working.
      const trimmed = formData.display_name.trim();
      const parts = trimmed.split(/\s+/);
      const first = parts[0] || '';
      const last  = parts.slice(1).join(' ') || '';

      const { error } = await supabase
        .from('user_profiles')
        .update({
          name: trimmed || userProfile.email,
          first_name: first,
          last_name: last,
          personal_profile: formData.bio.slice(0, BIO_MAX),
          timezone: formData.timezone || null,
          social: {
            ...(userProfile.social || {}),
            twitter: formData.twitter,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userProfile.user_id);
      if (error) throw error;

      clearDraft();
      setStatus({ type: 'success', message: 'Profile saved.' });
      setTimeout(() => {
        onUpdate?.();
        onClose();
      }, 900);
    } catch (error) {
      console.error('Error updating profile:', error);
      setStatus({ type: 'error', message: error.message || 'Failed to save profile.' });
    } finally {
      setLoading(false);
    }
  };

  const bioLeft = BIO_MAX - (formData.bio?.length || 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="bg-gray-950 border border-white/10 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Subtle grid backdrop for the terminal feel */}
            <div className="relative">
              <div className="pointer-events-none absolute inset-0 opacity-[0.05] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:32px_32px]" />

              {/* Header */}
              <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
                    ▸ Edit profile
                  </div>
                  <h3 className="text-2xl font-black text-white leading-tight">Who you are.</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Phone &amp; billing details live in <span className="text-gray-300">Security</span> and <span className="text-gray-300">Billing</span>.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-xl border border-white/10 text-gray-500 hover:text-white hover:border-white/20 transition-all"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>

              {status && (
                <div className={`relative mx-6 mb-4 px-4 py-3 rounded-xl border font-mono text-xs ${
                  status.type === 'success'
                    ? 'bg-green-500/5 border-green-500/30 text-green-300'
                    : 'bg-red-500/5 border-red-500/30 text-red-300'
                }`}>
                  {status.type === 'success' ? '✓' : '✗'} {status.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="relative px-6 pb-6 space-y-5">
                {/* Avatar */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-900 border border-white/10 flex items-center justify-center">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={FiUser} className="w-8 h-8 text-gray-600" />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 text-gray-900 hover:scale-110 transition-transform disabled:opacity-50 shadow-lg"
                    >
                      {uploadingAvatar
                        ? <SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" />
                        : <SafeIcon icon={FiCamera} className="w-3.5 h-3.5" />}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">Profile photo</div>
                    <div className="text-gray-500 text-xs font-mono">JPG · PNG · WebP · 5 MB max</div>
                  </div>
                </div>

                {/* Display name */}
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                    Display name
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => handleChange('display_name', e.target.value)}
                    placeholder="Jean-Michel Portal"
                    maxLength={80}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50 transition-colors"
                    required
                  />
                </div>

                {/* Bio */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      Trading bio
                    </label>
                    <span className={`font-mono text-[10px] ${bioLeft < 20 ? 'text-orange-400' : 'text-gray-600'}`}>
                      {bioLeft} chars left
                    </span>
                  </div>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => handleChange('bio', e.target.value.slice(0, BIO_MAX))}
                    placeholder="I trade Gold, Crypto & FX using multi-timeframe context."
                    rows={3}
                    maxLength={BIO_MAX}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50 transition-colors resize-none"
                  />
                </div>

                {/* Timezone */}
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                    Timezone
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => handleChange('timezone', e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400/50 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="">Use browser default</option>
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-gray-600 font-mono mt-1.5">
                    Used for daily streak &amp; discipline reports.
                  </p>
                </div>

                {/* Twitter (optional) */}
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                    X / Twitter <span className="normal-case text-gray-600">(optional)</span>
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiTwitter} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <input
                      type="text"
                      value={formData.twitter}
                      onChange={(e) => handleChange('twitter', e.target.value.replace(/^@/, ''))}
                      placeholder="yourhandle"
                      maxLength={30}
                      className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50 transition-colors"
                    />
                  </div>
                </div>

                {/* Save */}
                <div className="flex items-center justify-between pt-2 border-t border-white/5">
                  <div className="font-mono text-[10px] text-gray-600 tracking-widest uppercase">
                    Auto-saved as draft
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !formData.display_name.trim()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold shadow-lg shadow-cyan-500/20 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {loading ? (
                      <>
                        <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      'Save changes'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProfileModal;
